import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, IndianRupee, Package2, Check } from "lucide-react";

/**
 * Universal Material Library picker.
 *
 * Modes:
 *  - Single-pick (default): calls onPick(material) after clicking a row
 *  - Multi-select (multi=true): user checkboxes many rows, clicks "Add N items"
 *
 * Consumers: BOQ, Estimates, Procurement / PO drafts, Inventory intake,
 * Expense entry — anywhere a materials rate is needed.
 */
export default function MaterialPickerDialog({ open, onClose, onPick, onPickMany, multi = false }) {
  const [modules, setModules] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("");
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({}); // code -> material

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/material-library/modules"); setModules(data); } catch { /* ignore */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (q) p.append("q", q);
        if (module) p.append("module", module);
        p.append("limit", "150");
        const { data } = await api.get(`/material-library?${p.toString()}`);
        setItems(data);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [open, q, module]);

  useEffect(() => { if (!open) { setChecked({}); setQ(""); setModule(""); } }, [open]);

  const toggle = (m) => setChecked((c) => {
    const nc = { ...c };
    if (nc[m.item_code]) delete nc[m.item_code];
    else nc[m.item_code] = m;
    return nc;
  });

  const checkedList = useMemo(() => Object.values(checked), [checked]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package2 className="w-4 h-4 text-primary" /> Material Library</DialogTitle>
          <DialogDescription>Pick from the master catalog — description, unit and per-unit price populate automatically.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by name, code, category, spec…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="picker-search" autoFocus />
          </div>
          <Select value={module || "__all__"} onValueChange={(v) => setModule(v === "__all__" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="picker-module"><SelectValue placeholder="All modules" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All modules</SelectItem>
              {modules.map((m) => (
                <SelectItem key={m.module_code} value={m.module_code}>{m.module_code} · {m.module_name} ({m.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 overflow-auto border border-border/50 rounded-lg">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60 backdrop-blur text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <tr>
                {multi && <th className="w-8 py-2 px-2"></th>}
                <th className="text-left py-2 px-3 w-20">Code</th>
                <th className="text-left w-24">Module</th>
                <th className="text-left w-28">Category</th>
                <th className="text-left">Item</th>
                <th className="text-left w-24">UOM</th>
                <th className="text-right w-24 pr-3">Price (₹)</th>
                {!multi && <th className="w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={multi ? 7 : 8} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={multi ? 7 : 8} className="text-center py-8 text-muted-foreground">No matches.</td></tr>}
              {items.map((m) => {
                const priced = Number(m.per_unit_price || 0) > 0;
                return (
                  <tr key={m.item_code} data-testid={`picker-row-${m.item_code}`} className={`border-t border-border/50 ${multi ? "cursor-default" : "cursor-pointer"} hover:bg-muted/20 ${checked[m.item_code] ? "bg-primary/5" : ""}`}
                      onClick={() => { if (!multi) onPick && onPick(m); }}>
                    {multi && (
                      <td className="px-2 text-center">
                        <input type="checkbox" checked={!!checked[m.item_code]} onChange={() => toggle(m)} data-testid={`picker-check-${m.item_code}`} className="accent-primary" />
                      </td>
                    )}
                    <td className="py-1.5 px-3 font-mono text-[11px]">{m.item_code}</td>
                    <td className="text-[11px] text-muted-foreground">{m.module_code}</td>
                    <td className="text-xs">{m.category}</td>
                    <td className="font-medium">{m.name}{m.spec ? <span className="text-[11px] text-muted-foreground ml-1">· {m.spec}</span> : null}</td>
                    <td className="text-xs text-muted-foreground">{m.uom}</td>
                    <td className="text-right pr-3 font-medium">
                      {priced ? (
                        <span className="flex items-center justify-end gap-0.5"><IndianRupee className="w-3 h-3" />{Number(m.per_unit_price).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-amber-600">Set price</span>
                      )}
                    </td>
                    {!multi && (
                      <td className="pr-3 text-right">
                        <button className="text-primary text-[11px] hover:underline flex items-center gap-0.5 ml-auto" data-testid={`picker-use-${m.item_code}`}>
                          <Check className="w-3 h-3" /> Use
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {multi && (
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div className="text-xs text-muted-foreground">{checkedList.length} selected · {checkedList.filter(m => Number(m.per_unit_price||0) > 0).length} with prices</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button disabled={checkedList.length === 0} onClick={() => onPickMany && onPickMany(checkedList)} data-testid="picker-add-many" className="bg-primary text-primary-foreground">
                Add {checkedList.length || ""} item{checkedList.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
